import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { PageHeader } from '../portfolio/PortfolioCommon'
import { Breadcrumbs } from '../components/ui/Breadcrumbs'
import { getSupabaseBrowserClient } from '../lib/supabase'
import {
  customerSafeOperationMessage,
  getConfirmationCoordination, recallConfirmation, recallInformationRequest, reassignConfirmation, reassignInformationRequest,
  respondConfirmation, startConfirmationReassessment, withdrawConfirmation,
  withdrawInformationRequest,
} from './assessment-api'
import { customerHistoryLabel } from './customer-history-label'
import { DecisionBriefChecklist, DecisionBriefSection } from './DecisionBrief'
import { buildConfirmationBriefGuidance } from './confirmation-guidance'
import { InformationRequestComposer, manageContextualInvitation } from './ContextualPersonAssignment'

type RpcError = { message: string }
type RpcResponse = PromiseLike<{ data: unknown; error: RpcError | null }>
type RpcInvoker = (functionName: string, args?: Record<string, unknown>) => RpcResponse
function object(value: unknown): Record<string, unknown> { return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {} }
function objects(value: unknown): Record<string, unknown>[] { return Array.isArray(value) ? value.map(object) : [] }
function text(value: unknown): string { return typeof value === 'string' ? value : '' }

const factExplanations: Record<string, string> = {
  'What could go wrong?': 'The realistic impact if this AI use makes a mistake, fails or produces an incorrect result.',
  'Role in decisions about people': "Whether the AI informs or makes decisions that can affect a person's opportunities, access, rights or treatment.",
  'Meaningful human decision review': 'Whether a person can understand the relevant information and choose a different outcome before it affects someone.',
  'Correction and human review': 'Whether a person affected by a wrong outcome has a clear way to ask for correction or human review.',
  'Scale of use': 'The approximate number of people, transactions or material outputs the AI could affect in a typical month.',
  'Information involved': 'The kinds of information the AI receives, creates or infers, including personal, confidential or sensitive information.',
  'Use of personal information': 'Whether the AI will use personal information for a different purpose from why it was originally collected.',
  'Rules or obligations that apply': 'The laws, regulations, standards, contracts or professional duties that may affect how this AI use can be used.',
}

const factDisplayLabels: Record<string, string> = {
  'Credible consequences': 'What could go wrong?',
  'Legal, regulatory or professional context': 'Rules or obligations that apply',
}

async function getTask(requirementId: string) {
  const client = getSupabaseBrowserClient() as unknown as { rpc: RpcInvoker }
  const { data, error } = await client.rpc('get_r1_confirmation_task', { p_requirement_id: requirementId })
  if (error) throw new Error(error.message)
  const row = object(data)
  return {
    requirement: object(row.requirement),
    aiUseCase: object(row.aiUseCase),
    assessment: object(row.assessment),
    organisationName: text(row.organisationName),
    responses: objects(row.responses),
    informationRequests: objects(row.informationRequests),
    recipients: objects(row.informationRequestRecipients),
  }
}

const factDecisions = [
  { code: 'confirmed', label: 'The recorded fact is correct', description: 'Confirm the supplied fact can be relied upon for this Assessment.' },
  { code: 'changed', label: 'The recorded fact needs to change', description: 'Provide the corrected fact before completing your confirmation.' },
]
const judgementDecisions = [
  { code: 'yes', label: 'Yes', description: 'The requested judgement is supported without additional conditions.' },
  { code: 'yes_with_conditions', label: 'Yes, with conditions', description: 'The AI use may proceed only after the conditions you record are met.' },
  { code: 'no', label: 'No', description: 'The requested judgement is not supported in the current arrangements.' },
]
const professionalDecisions = [
  { code: 'acceptable', label: 'Acceptable', description: 'No additional requirements were identified within your specialist authority.' },
  { code: 'acceptable_with_conditions', label: 'Acceptable with conditions', description: 'The arrangements are acceptable only when the conditions you record are satisfied.' },
  { code: 'not_acceptable', label: 'Not acceptable', description: 'The current arrangements should not proceed within the area you are confirming.' },
]
const securityDecisions = [
  { code: 'acceptable', label: 'Security requirements met', description: 'I have not identified any additional security requirements.' },
  { code: 'acceptable_with_conditions', label: 'Security requirements met with conditions', description: 'The security arrangements are adequate only if the conditions I record are completed.' },
  { code: 'not_acceptable', label: 'Security requirements not met', description: 'The security arrangements are not adequate and the issues I record must be resolved.' },
]

const confirmationLabels: Record<string, string> = {
  product: 'Product information confirmation',
  technical: 'Technical confirmation',
  security: 'Security confirmation',
  privacy: 'Privacy confirmation',
  legal_professional: 'Legal or professional confirmation',
}

type ConfirmationTab = 'review' | 'facts' | 'response'
type ConfirmationAction = 'reassign' | 'recall' | 'withdraw'

const confirmationTabs: { id: ConfirmationTab; label: string }[] = [
  { id: 'review', label: '1. Review' },
  { id: 'facts', label: '2. Assessment information' },
  { id: 'response', label: '3. Your response' },
]

const reviewerRoles: Record<string, string> = {
  product: 'Product reviewer',
  technical: 'Technical reviewer',
  security: 'Security reviewer',
  privacy: 'Privacy reviewer',
  legal_professional: 'Legal or professional reviewer',
}

const reviewerCopy: Record<string, { explanation: string; introduction: string }> = {
  product: {
    explanation: 'You’re reviewing the Product information for this AI use.',
    introduction: 'Use these questions to decide whether the Product information is accurate and adequate.',
  },
  technical: {
    explanation: 'You’re reviewing the technical aspects of this AI use.',
    introduction: 'Use these questions to decide whether the technical arrangements are adequate.',
  },
  security: {
    explanation: 'You’re reviewing the security aspects of this AI use.',
    introduction: 'Use these questions to decide whether the security arrangements are adequate.',
  },
  privacy: {
    explanation: 'You’re reviewing the privacy aspects of this AI use.',
    introduction: 'Use these questions to decide whether the privacy arrangements are adequate.',
  },
  legal_professional: {
    explanation: 'You’re reviewing the legal or professional aspects of this AI use.',
    introduction: 'Use these questions to decide whether the legal or professional requirements are adequately addressed.',
  },
}

const statusLabels: Record<string, string> = {
  awaiting_response: 'Awaiting your response',
  information_requested: 'Waiting for information',
  completed: 'Complete',
  reviewer_needs_assignment: 'Needs reviewer',
  withdrawn: 'Withdrawn',
}

function checklistLabel(value: string): ReactNode {
  const separator = value.indexOf(':')
  if (separator < 1) return value
  return <><strong>{value.slice(0, separator)}:</strong>{value.slice(separator + 1)}</>
}

const informationRequestCancellationLabels: Record<string, string> = {
  information_no_longer_required: 'Information no longer required',
  wrong_person: 'Asked the wrong person',
  entered_in_error: 'Request entered in error',
  assessment_changed: 'Assessment changed',
  other: 'Other',
}

function SafeConfirmationError() {
  return <section className="panel assessment-safe-error" role="alert"><h2>We couldn't load this confirmation.</h2><p>Your information has not been changed. Please try again.</p></section>
}

export function ConfirmationOperationError({ error }: { error: unknown }) {
  return <section className="inline-alert danger" role="alert">
    <strong>That action could not be completed.</strong>
    <p>{customerSafeOperationMessage(error)}</p>
  </section>
}

export function ConfirmationTaskPage() {
  const { requirementId = '' } = useParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [decision, setDecision] = useState('')
  const [comments, setComments] = useState('')
  const [conditions, setConditions] = useState('')
  const [reason, setReason] = useState('')
  const [changedValue, setChangedValue] = useState('')
  const [evidenceReference, setEvidenceReference] = useState('')
  const [assigneeId, setAssigneeId] = useState('')
  const [confirmationTransferReason, setConfirmationTransferReason] = useState('')
  const [confirmationRecallReason, setConfirmationRecallReason] = useState('')
  const [confirmationWithdrawalReason, setConfirmationWithdrawalReason] = useState('')
  const [confirmationWithdrawalExplanation, setConfirmationWithdrawalExplanation] = useState('')
  const [confirmationWithdrawalConfirmed, setConfirmationWithdrawalConfirmed] = useState(false)
  const [reassessmentReason, setReassessmentReason] = useState('')
  const [requestRecipientById, setRequestRecipientById] = useState<Record<string, string>>({})
  const [requestTransferReasonById, setRequestTransferReasonById] = useState<Record<string, string>>({})
  const [requestRecallReasonById, setRequestRecallReasonById] = useState<Record<string, string>>({})
  const [requestWithdrawalById, setRequestWithdrawalById] = useState<Record<string, string>>({})
  const [requestWithdrawalExplanationById, setRequestWithdrawalExplanationById] = useState<Record<string, string>>({})
  const [invitationMessageByRequestId, setInvitationMessageByRequestId] = useState<Record<string, string>>({})
  const [activeTab, setActiveTab] = useState<ConfirmationTab>('review')
  const [confirmationActionsOpen, setConfirmationActionsOpen] = useState(false)
  const [activeConfirmationAction, setActiveConfirmationAction] = useState<ConfirmationAction | null>(null)
  const confirmationActionsRef = useRef<HTMLDivElement>(null)
  const confirmationActionsTriggerRef = useRef<HTMLButtonElement>(null)

  const taskQuery = useQuery({ queryKey: ['r1-confirmation-task', requirementId], queryFn: () => getTask(requirementId), enabled: Boolean(requirementId) })
  const coordinationQuery = useQuery({ queryKey: ['r1-confirmation-coordination', requirementId], queryFn: () => getConfirmationCoordination(requirementId), enabled: Boolean(requirementId) })
  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['r1-confirmation-task', requirementId] }),
      queryClient.invalidateQueries({ queryKey: ['r1-confirmation-coordination', requirementId] }),
      queryClient.invalidateQueries({ queryKey: ['r1-my-actions'] }),
    ])
  }
  const respondMutation = useMutation({
    mutationFn: () => respondConfirmation(requirementId, decision, {
      confirmedValue: decision === 'changed' ? { correctedValue: changedValue } : undefined,
      comments, conditions, reason, evidenceReference,
    }),
    onSuccess: () => void refresh(),
  })
  const reassignConfirmationMutation = useMutation({
    mutationFn: () => reassignConfirmation(
      requirementId,
      coordination?.assignmentRevision ?? 0,
      assigneeId,
      confirmationTransferReason,
    ),
    onSuccess: () => {
      setAssigneeId('')
      setConfirmationTransferReason('')
      setActiveConfirmationAction(null)
      void refresh()
    },
  })
  const recallConfirmationMutation = useMutation({
    mutationFn: () => recallConfirmation(
      requirementId,
      coordination?.assignmentRevision ?? 0,
      confirmationRecallReason,
    ),
    onSuccess: () => {
      setConfirmationRecallReason('')
      setActiveConfirmationAction(null)
      void refresh()
    },
  })
  const withdrawConfirmationMutation = useMutation({
    mutationFn: () => withdrawConfirmation(requirementId, confirmationWithdrawalReason, confirmationWithdrawalExplanation),
    onSuccess: () => {
      setConfirmationWithdrawalReason('')
      setConfirmationWithdrawalExplanation('')
      setConfirmationWithdrawalConfirmed(false)
      setActiveConfirmationAction(null)
      void refresh()
    },
  })
  const reassignRequestMutation = useMutation({
    mutationFn: ({ requestId, assignmentRevision, membershipId, reason }: { requestId: string; assignmentRevision: number; membershipId: string; reason: string }) =>
      reassignInformationRequest(requestId, assignmentRevision, membershipId, reason),
    onSuccess: () => void refresh(),
  })
  const recallRequestMutation = useMutation({
    mutationFn: ({ requestId, assignmentRevision, reason }: { requestId: string; assignmentRevision: number; reason: string }) =>
      recallInformationRequest(requestId, assignmentRevision, reason),
    onSuccess: () => void refresh(),
  })
  const withdrawRequestMutation = useMutation({ mutationFn: ({ requestId, reason, explanation }: { requestId: string; reason: string; explanation: string }) => withdrawInformationRequest(requestId, reason, explanation), onSuccess: () => void refresh() })
  const manageInvitationMutation = useMutation({
    mutationFn: ({ requestId, operation }: { requestId: string; operation: 'resend' | 'cancel' }) => manageContextualInvitation({
      operation,
      targetType: 'information_request',
      targetId: requestId,
    }),
    onSuccess: (result, variables) => {
      setInvitationMessageByRequestId((current) => ({
        ...current,
        [variables.requestId]: result.operation === 'cancel'
          ? 'Recipient invitation cancelled for this request. The request remains open and blocked until it is reassigned or validly withdrawn.'
          : result.warning ?? 'Invitation resent. This request remains blocked until the invited person securely joins.',
      }))
      void refresh()
    },
  })
  const reassessmentMutation = useMutation({
    mutationFn: () => startConfirmationReassessment(requirementId, reassessmentReason),
    onSuccess: (result) => {
      const assessmentId = text(result.assessmentId)
      void navigate(`/assess/${text(task?.aiUseCase.id)}?reassessment=${assessmentId || 'started'}`)
    },
  })

  const task = taskQuery.data
  const requirement = task?.requirement ?? {}
  const confirmationKind = text(requirement.confirmation_kind)
  const confirmationType = text(requirement.confirmation_type)
  const status = text(requirement.status)
  const sourceSnapshot = requirement.source_snapshot
  const taskTitle = confirmationLabels[confirmationType] ?? confirmationType.replaceAll('_', ' ') + ' confirmation'
  const briefGuidance = buildConfirmationBriefGuidance(confirmationType, sourceSnapshot)
  const isOpen = ['awaiting_response', 'information_requested'].includes(status)
  const openInfo = task?.informationRequests.filter((item) => text(item.status) === 'open') ?? []
  const coordination = coordinationQuery.data
  const recipientName = (membershipId: string, invitedName = '') => text(coordination?.eligibleRecipients.find((item) => text(item.membershipId) === membershipId)?.displayName) || invitedName || 'the selected person'
  const decisions = confirmationKind === 'fact'
    ? factDecisions
    : confirmationType === 'security'
      ? securityDecisions
    : confirmationType === 'legal_professional' || confirmationType === 'privacy'
      ? professionalDecisions
      : judgementDecisions
  const queryError = taskQuery.error ?? coordinationQuery.error
  const operationError = respondMutation.error ?? reassignConfirmationMutation.error ?? recallConfirmationMutation.error
    ?? withdrawConfirmationMutation.error ?? reassignRequestMutation.error ?? recallRequestMutation.error ?? withdrawRequestMutation.error ?? manageInvitationMutation.error ?? reassessmentMutation.error
  const error = queryError ?? operationError

  useEffect(() => {
    if (error) console.error('Specialist confirmation operation failed', error)
  }, [error])

  useEffect(() => {
    if (!confirmationActionsOpen) return
    const closeOnOutsideClick = (event: PointerEvent) => {
      if (event.target instanceof Node && !confirmationActionsRef.current?.contains(event.target)) setConfirmationActionsOpen(false)
    }
    const closeOnEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return
      setConfirmationActionsOpen(false)
      confirmationActionsTriggerRef.current?.focus()
    }
    document.addEventListener('pointerdown', closeOnOutsideClick)
    document.addEventListener('keydown', closeOnEscape)
    return () => {
      document.removeEventListener('pointerdown', closeOnOutsideClick)
      document.removeEventListener('keydown', closeOnEscape)
    }
  }, [confirmationActionsOpen])

  if (taskQuery.isLoading) return <div className="page-stack"><section className="panel"><p>Loading specialist confirmation…</p></section></div>
  if (!task) return <div className="page-stack"><SafeConfirmationError /></div>

  const submitResponse = (event: FormEvent) => {
    event.preventDefault()
    respondMutation.mutate()
  }
  const needsCondition = decision === 'yes_with_conditions' || decision === 'acceptable_with_conditions'
  const needsReason = decision === 'no' || decision === 'not_acceptable' || decision === 'changed'
  const focusCodes = new Set(briefGuidance.focusFacts.map((fact) => fact.code))
  const immediatelyRelevantFacts = briefGuidance.focusFacts.length ? briefGuidance.focusFacts : briefGuidance.facts
  const remainingFacts = briefGuidance.facts.filter((fact) => !focusCodes.has(fact.code))
  const reviewerRole = reviewerRoles[confirmationType] ?? (text(requirement.required_authority) || 'Specialist reviewer')
  const presentation = reviewerCopy[confirmationType] ?? {
    explanation: 'You’re reviewing the specialist aspects of this AI use.',
    introduction: 'Use these questions to decide whether the relevant specialist requirements are adequately addressed.',
  }
  const roleExplanation = confirmationType === 'security'
    ? 'You’re reviewing the security aspects of this AI use. Your response will help complete the assessment; it does not approve the overall AI use.'
    : `${presentation.explanation} Your response will help complete the assessment; it does not approve the overall AI use.`
  const reviewIntroduction = confirmationType === 'security'
    ? 'Review the security arrangements below and decide whether they are adequate for this use.'
    : `${presentation.introduction} If you cannot answer one, request more information before responding.`
  const statusLabel = statusLabels[status] ?? status.replaceAll('_', ' ')
  const canManageConfirmation = isOpen && Boolean(coordination?.canTransferConfirmation || coordination?.canRecallConfirmation || coordination?.canCoordinateConfirmation)
  const factList = (facts: typeof briefGuidance.facts) => <dl className="decision-brief-context-list">
    {facts.map((fact) => <div key={fact.code}>
      <dt>{factDisplayLabels[fact.label] ?? fact.label}</dt>
      {factExplanations[factDisplayLabels[fact.label] ?? fact.label] ? <p className="decision-brief-fact-explanation">{factExplanations[factDisplayLabels[fact.label] ?? fact.label]}</p> : null}
      <dd>{fact.value}</dd>
    </div>)}
  </dl>
  const selectAdjacentTab = (event: KeyboardEvent<HTMLButtonElement>, currentTab: ConfirmationTab) => {
    const currentIndex = confirmationTabs.findIndex((tab) => tab.id === currentTab)
    let nextIndex = currentIndex
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % confirmationTabs.length
    else if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + confirmationTabs.length) % confirmationTabs.length
    else if (event.key === 'Home') nextIndex = 0
    else if (event.key === 'End') nextIndex = confirmationTabs.length - 1
    else return
    event.preventDefault()
    const nextTab = confirmationTabs[nextIndex]!.id
    setActiveTab(nextTab)
    document.getElementById(`confirmation-tab-${nextTab}`)?.focus()
  }
  const openResponseTab = () => {
    setActiveTab('response')
    window.requestAnimationFrame(() => document.getElementById('open-information-requests')?.focus())
  }
  const focusConfirmationAction = (position: 'first' | 'last') => {
    window.requestAnimationFrame(() => {
      const items = confirmationActionsRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]')
      if (!items?.length) return
      items[position === 'first' ? 0 : items.length - 1]?.focus()
    })
  }
  const openConfirmationActions = (position: 'first' | 'last' = 'first') => {
    setConfirmationActionsOpen(true)
    focusConfirmationAction(position)
  }
  const selectConfirmationAction = (action: ConfirmationAction) => {
    setConfirmationActionsOpen(false)
    setActiveConfirmationAction(action)
    window.requestAnimationFrame(() => document.getElementById(`confirmation-action-${action}`)?.focus())
  }
  const navigateConfirmationActions = (event: KeyboardEvent<HTMLDivElement>) => {
    const items = Array.from(event.currentTarget.querySelectorAll<HTMLButtonElement>('[role="menuitem"]'))
    const currentIndex = items.indexOf(document.activeElement as HTMLButtonElement)
    let nextIndex = currentIndex
    if (event.key === 'ArrowDown') nextIndex = (currentIndex + 1) % items.length
    else if (event.key === 'ArrowUp') nextIndex = (currentIndex - 1 + items.length) % items.length
    else if (event.key === 'Home') nextIndex = 0
    else if (event.key === 'End') nextIndex = items.length - 1
    else return
    event.preventDefault()
    items[nextIndex]?.focus()
  }

  return (
    <div className="page-stack decision-brief-page" data-deployment-version="confirmation-brief-v2">
      <Breadcrumbs items={[{ label: 'AI Uses', to: '/ai-register' }, { label: text(task.aiUseCase.title), to: `/ai-register/${text(task.aiUseCase.id)}` }, { label: taskTitle }]} />
      <PageHeader
        eyebrow="Specialist confirmation"
        title={taskTitle}
        description="Please review the assessment information and record your response."
      />
      {queryError ? <SafeConfirmationError /> : null}
      {operationError ? <ConfirmationOperationError error={operationError} /> : null}

      <div className="confirmation-summary-shell">
        <DecisionBriefSection
          icon="document"
          eyebrow="AI use"
          title={<Link className="decision-brief-title-link" to={`/ai-register/${text(task.aiUseCase.id)}`}>{text(task.aiUseCase.title)}</Link>}
          description={text(task.aiUseCase.businessPurpose)}
        >
          <dl className="confirmation-summary-grid">
            <div><dt>Business area</dt><dd>{text(task.aiUseCase.businessArea) || 'Not recorded'}</dd></div>
            <div><dt>Business owner</dt><dd>{text(task.aiUseCase.businessOwner) || 'Not recorded'}</dd></div>
            <div><dt>Your role</dt><dd>{reviewerRole}</dd></div>
            <div><dt>Status</dt><dd><span className={`confirmation-status-chip confirmation-status-${status}`}>{statusLabel}</span></dd></div>
          </dl>
        </DecisionBriefSection>
        {canManageConfirmation ? <div className="confirmation-more-actions" ref={confirmationActionsRef}>
          <button
            ref={confirmationActionsTriggerRef}
            className="confirmation-more-trigger"
            type="button"
            aria-label="More confirmation actions"
            aria-haspopup="menu"
            aria-expanded={confirmationActionsOpen}
            onClick={() => setConfirmationActionsOpen((open) => !open)}
            onKeyDown={(event) => {
              if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                openConfirmationActions('first')
              } else if (event.key === 'ArrowUp') {
                event.preventDefault()
                openConfirmationActions('last')
              }
            }}
          ><span aria-hidden="true">•••</span></button>
          {confirmationActionsOpen ? <div className="confirmation-actions-menu" role="menu" aria-label="Confirmation actions" onKeyDown={navigateConfirmationActions}>
            {coordination?.canTransferConfirmation ? <button type="button" role="menuitem" onClick={() => selectConfirmationAction('reassign')}>Reassign specialist</button> : null}
            {coordination?.canRecallConfirmation ? <button type="button" role="menuitem" onClick={() => selectConfirmationAction('recall')}>Recall assignment</button> : null}
            {coordination?.canCoordinateConfirmation ? <button className="danger-text" type="button" role="menuitem" onClick={() => selectConfirmationAction('withdraw')}>Withdraw confirmation request</button> : null}
          </div> : null}
        </div> : null}
      </div>

      {activeConfirmationAction ? <section id={`confirmation-action-${activeConfirmationAction}`} className={`panel confirmation-action-form${activeConfirmationAction === 'withdraw' ? ' destructive' : ''}`} tabIndex={-1} aria-labelledby="confirmation-action-heading">
        <header><div><p className="eyebrow">More actions</p><h2 id="confirmation-action-heading">{activeConfirmationAction === 'reassign' ? 'Reassign specialist' : activeConfirmationAction === 'recall' ? 'Recall assignment' : 'Withdraw confirmation request'}</h2></div><button className="button text compact" type="button" aria-label="Close confirmation action" onClick={() => setActiveConfirmationAction(null)}>Close</button></header>
        {activeConfirmationAction === 'reassign' ? <div className="form-stack">
          <p className="muted">Move this request to another eligible specialist. Existing history is retained. This does not answer or complete the confirmation.</p>
          <label>New person<select value={assigneeId} onChange={(event) => setAssigneeId(event.target.value)}>
            <option value="">Choose an active organisation member…</option>
            {coordination?.eligibleReviewers.filter((member) => text(member.membershipId) !== text(requirement.assigned_membership_id)).map((member) => <option key={text(member.membershipId)} value={text(member.membershipId)}>{text(member.displayName)}</option>)}
          </select></label>
          <label>Reason<textarea rows={3} required value={confirmationTransferReason} onChange={(event) => setConfirmationTransferReason(event.target.value)} /></label>
          <button className="button secondary" type="button" disabled={!assigneeId || !confirmationTransferReason.trim() || reassignConfirmationMutation.isPending} onClick={() => reassignConfirmationMutation.mutate()}>Reassign specialist</button>
        </div> : null}
        {activeConfirmationAction === 'recall' ? <div className="form-stack">
          <p className="muted">Return the request to the sender before the specialist responds. The same confirmation can be issued again, and existing history is retained.</p>
          <label>Reason<textarea rows={3} required value={confirmationRecallReason} onChange={(event) => setConfirmationRecallReason(event.target.value)} /></label>
          <button className="button text" type="button" disabled={!confirmationRecallReason.trim() || recallConfirmationMutation.isPending} onClick={() => recallConfirmationMutation.mutate()}>Recall assignment</button>
        </div> : null}
        {activeConfirmationAction === 'withdraw' ? <div className="form-stack">
          <p>Close this specialist request because it is no longer required. Existing history is retained. Swell will keep any confirmation that still applies.</p>
          <label>Withdrawal reason<select value={confirmationWithdrawalReason} onChange={(event) => setConfirmationWithdrawalReason(event.target.value)}>
            <option value="">Choose a reason…</option><option value="assigned_in_error">Assigned in error</option><option value="confirmation_no_longer_required">Confirmation no longer required</option><option value="assessment_changed">Assessment changed</option><option value="duplicate_request">Duplicate request</option><option value="other">Other</option>
          </select></label>
          {confirmationWithdrawalReason === 'other' ? <label>Explanation<textarea rows={3} required value={confirmationWithdrawalExplanation} onChange={(event) => setConfirmationWithdrawalExplanation(event.target.value)} /></label> : null}
          <label className="actions-withdrawal-confirmation"><input type="checkbox" checked={confirmationWithdrawalConfirmed} onChange={(event) => setConfirmationWithdrawalConfirmed(event.target.checked)} /><span>I understand this closes the current request while retaining its governed history.</span></label>
          <button className="button danger-outline" type="button" disabled={!confirmationWithdrawalReason || (confirmationWithdrawalReason === 'other' && !confirmationWithdrawalExplanation.trim()) || !confirmationWithdrawalConfirmed || withdrawConfirmationMutation.isPending} onClick={() => withdrawConfirmationMutation.mutate()}>Withdraw confirmation request</button>
        </div> : null}
      </section> : null}

      {openInfo.length > 0 ? <section className="panel confirmation-blocker" aria-labelledby="confirmation-blocker-heading">
        <div><p className="eyebrow">Waiting</p><h2 id="confirmation-blocker-heading">Waiting for information from {openInfo.map((item) => recipientName(text(item.requested_from_membership_id), text(item.recipient_invitation_display_name))).join(', ')}</h2><p>{openInfo.length === 1 ? 'This request' : 'These requests'} must be answered or validly withdrawn before you can complete the confirmation.</p></div>
        <button className="button secondary" type="button" onClick={openResponseTab}>View information request</button>
      </section> : null}

      <div className="confirmation-tabs" role="tablist" aria-label="Specialist confirmation details">
        {confirmationTabs.map((tab) => <button
          key={tab.id}
          id={`confirmation-tab-${tab.id}`}
          className="confirmation-tab"
          type="button"
          role="tab"
          aria-selected={activeTab === tab.id}
          aria-controls={`confirmation-panel-${tab.id}`}
          aria-describedby={tab.id === 'response' && openInfo.length > 0 ? 'confirmation-response-status' : undefined}
          tabIndex={activeTab === tab.id ? 0 : -1}
          onClick={() => setActiveTab(tab.id)}
          onKeyDown={(event) => selectAdjacentTab(event, tab.id)}
        ><span>{tab.label}</span>{tab.id === 'response' && openInfo.length > 0 ? <small id="confirmation-response-status">Waiting</small> : null}</button>)}
      </div>

      <div id="confirmation-panel-review" className="confirmation-tab-panel" role="tabpanel" aria-labelledby="confirmation-tab-review" tabIndex={0} hidden={activeTab !== 'review'}>

      <DecisionBriefSection
        icon="shield"
        eyebrow="Your role in this decision"
        title="Why you've been asked"
        description={confirmationType === 'security' ? 'The assessment raised concerns relating to system access and the use of sensitive information that will require a security review.' : text(requirement.reason)}
        tone="attention"
      >
        <p className="decision-brief-callout">{roleExplanation}</p>
        {confirmationType === 'security' ? <p>Review the security arrangements below and decide whether they are adequate for this use.</p> : <p>The assessment identified questions that need a specialist review before the next steps can be completed. Your response informs the assessment rather than the final approval of the AI use.</p>}
      </DecisionBriefSection>

      <DecisionBriefSection
        icon="info"
        eyebrow="Review guide"
        title="What to consider"
        description={reviewIntroduction}
      >
        <DecisionBriefChecklist items={briefGuidance.considerations.map((item) => ({ label: checklistLabel(item), status: 'neutral' as const }))} />
      </DecisionBriefSection>

      <DecisionBriefSection
        icon="workflow"
        title="How your confirmation is used"
        description="Your response becomes part of the assessment record and is available to the people making the final approval decision."
      >
        <p>Your response applies only to the {confirmationType === 'security' ? 'security matters' : 'specialist matters'} you reviewed. It does not approve the overall AI use.</p>
      </DecisionBriefSection>
      </div>

      <div id="confirmation-panel-facts" className="confirmation-tab-panel" role="tabpanel" aria-labelledby="confirmation-tab-facts" tabIndex={0} hidden={activeTab !== 'facts'}>

      <DecisionBriefSection
        icon="document"
        eyebrow="Assessment context"
        title={confirmationType === 'security' ? 'Relevant to your security review' : 'Relevant to your review'}
        description="These answers were provided during the assessment. If anything is unclear, incomplete or incorrect, request more information before responding."
      >
        {briefGuidance.hasConflictingConsequences ? <div className="inline-alert danger" role="alert">
          <strong>Clarification required:</strong> the recorded consequences say both that an error may have a consequence and that any error would be minor. Do not rely on this answer without asking the owner to clarify it.
        </div> : null}
        {immediatelyRelevantFacts.length > 0 ? factList(immediatelyRelevantFacts) : <p className="muted">No assessment answers were provided for this review.</p>}
        {remainingFacts.length > 0 ? <details className="decision-brief-disclosure"><summary>Show other assessment information</summary>{factList(remainingFacts)}</details> : null}
      </DecisionBriefSection>
      </div>

      <div id="confirmation-panel-response" className="confirmation-tab-panel" role="tabpanel" aria-labelledby="confirmation-tab-response" tabIndex={0} hidden={activeTab !== 'response'}>

      <section id="open-information-requests" className="panel information-request-management" aria-labelledby="information-heading" tabIndex={-1}>
        <h2 id="information-heading">Information requests</h2>
        {task.informationRequests.length > 0 ? <div className="form-stack">
          {task.informationRequests.map((item) => <article key={text(item.id)}>
            <h3>{text(item.request_text)}</h3>
            <p className="muted">{text(item.status) === 'answered' ? 'Answered ✓' : text(item.status) === 'cancelled' ? 'Cancelled · No response required' : `Requested ${new Date(text(item.requested_at)).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}`}</p>
            {text(item.status) === 'cancelled' ? <div className="inline-alert neutral information-request-cancelled" role="status">
              <strong>Request cancelled</strong>
              <p>No response is required. The request remains in the governed Assessment history.</p>
              {informationRequestCancellationLabels[text(item.cancellation_reason)] ? <p><strong>Reason:</strong> {informationRequestCancellationLabels[text(item.cancellation_reason)]}</p> : null}
              {text(item.cancellation_explanation) ? <p>{text(item.cancellation_explanation)}</p> : null}
            </div> : null}
            {text(item.requested_from_membership_id) === '' && text(item.recipient_invitation_id) ? <p className="muted">Waiting for {text(item.recipient_invitation_display_name) || 'the invited person'} to join Swell before this request becomes actionable.</p> : null}
            {text(item.status) === 'open' && text(item.requested_from_membership_id) === '' && !text(item.recipient_invitation_id) ? <p className="muted">No recipient is currently assigned. The request remains open and blocking until an administrator assigns a recipient or it is validly withdrawn.</p> : null}
            {invitationMessageByRequestId[text(item.id)] ? <div className="inline-alert neutral" aria-live="polite">{invitationMessageByRequestId[text(item.id)]}</div> : null}
            {item.response_text ? <p>{text(item.response_text)}</p> : null}
            {item.evidence_reference ? <p><strong>Evidence:</strong> {text(item.evidence_reference)}</p> : null}
            {text(item.status) === 'open' && coordination?.canManageInformationRequests ? <div className="information-request-controls">
              {coordination.canCoordinateInformationRequestAssignments && text(item.requested_from_membership_id) === '' && text(item.recipient_invitation_id) ? <details className="information-request-invitation-controls">
                <summary className="button secondary compact">Manage invitation</summary>
                <div className="form-stack">
                  <p className="muted">Resending does not change the governed assignment. Use Recall below to clear this pending recipient safely.</p>
                  <button className="button secondary" type="button" disabled={manageInvitationMutation.isPending} onClick={() => manageInvitationMutation.mutate({ requestId: text(item.id), operation: 'resend' })}>
                    {manageInvitationMutation.isPending && manageInvitationMutation.variables?.requestId === text(item.id) && manageInvitationMutation.variables?.operation === 'resend' ? 'Resending…' : 'Resend invitation'}
                  </button>
                </div>
              </details> : null}
              <div className="button-row information-request-contextual-actions">
                {coordination.canCoordinateInformationRequestAssignments ? <details className="information-request-reassign-controls">
                  <summary className="button secondary compact">Reassign recipient</summary>
                  <div className="form-stack">
                    <label>Reassign recipient
                      <select value={requestRecipientById[text(item.id)] ?? ''} onChange={(event) => setRequestRecipientById((current) => ({ ...current, [text(item.id)]: event.target.value }))}>
                        <option value="">Choose an active member…</option>
                        {coordination.eligibleRecipients.filter((member) => text(member.membershipId) !== text(item.requested_from_membership_id)).map((member) => <option key={text(member.membershipId)} value={text(member.membershipId)}>{text(member.displayName)}</option>)}
                      </select>
                    </label>
                    <label>Reason
                      <textarea rows={3} required value={requestTransferReasonById[text(item.id)] ?? ''} onChange={(event) => setRequestTransferReasonById((current) => ({ ...current, [text(item.id)]: event.target.value }))} />
                    </label>
                    <button className="button secondary" type="button" disabled={!requestRecipientById[text(item.id)] || !requestTransferReasonById[text(item.id)]?.trim() || reassignRequestMutation.isPending} onClick={() => reassignRequestMutation.mutate({
                      requestId: text(item.id),
                      assignmentRevision: Number(item.assignment_revision) || 0,
                      membershipId: requestRecipientById[text(item.id)] ?? '',
                      reason: requestTransferReasonById[text(item.id)] ?? '',
                    })}>Reassign</button>
                  </div>
                </details> : null}
                {coordination.canCoordinateInformationRequestAssignments && (text(item.requested_from_membership_id) || text(item.recipient_invitation_id)) ? <details className="information-request-recall-controls">
                  <summary className="button text compact">Recall assignment</summary>
                  <div className="form-stack">
                    <p className="muted">Recall removes the current recipient but keeps this information request open and blocking.</p>
                    <label>Reason
                      <textarea rows={3} required value={requestRecallReasonById[text(item.id)] ?? ''} onChange={(event) => setRequestRecallReasonById((current) => ({ ...current, [text(item.id)]: event.target.value }))} />
                    </label>
                    <button className="button text" type="button" disabled={!requestRecallReasonById[text(item.id)]?.trim() || recallRequestMutation.isPending} onClick={() => recallRequestMutation.mutate({
                      requestId: text(item.id),
                      assignmentRevision: Number(item.assignment_revision) || 0,
                      reason: requestRecallReasonById[text(item.id)] ?? '',
                    })}>Recall assignment</button>
                  </div>
                </details> : null}
                <details className="information-request-withdraw-controls">
                  <summary className="button text compact">Withdraw request</summary>
                  <div className="form-stack">
                    <p className="muted">Withdrawal closes this request without deleting its governed history.</p>
                    <label>Withdrawal reason
                      <select value={requestWithdrawalById[text(item.id)] ?? ''} onChange={(event) => setRequestWithdrawalById((current) => ({ ...current, [text(item.id)]: event.target.value }))}>
                        <option value="">Choose a reason…</option><option value="information_no_longer_required">Information no longer required</option><option value="wrong_person">Asked the wrong person</option><option value="entered_in_error">Request entered in error</option><option value="assessment_changed">Assessment changed</option><option value="other">Other</option>
                      </select>
                    </label>
                    {requestWithdrawalById[text(item.id)] === 'other' ? <label>Explanation<textarea rows={3} required value={requestWithdrawalExplanationById[text(item.id)] ?? ''} onChange={(event) => setRequestWithdrawalExplanationById((current) => ({ ...current, [text(item.id)]: event.target.value }))} /></label> : null}
                    <button className="button text" type="button" disabled={!requestWithdrawalById[text(item.id)] || (requestWithdrawalById[text(item.id)] === 'other' && !requestWithdrawalExplanationById[text(item.id)]?.trim()) || withdrawRequestMutation.isPending} onClick={() => withdrawRequestMutation.mutate({ requestId: text(item.id), reason: requestWithdrawalById[text(item.id)] ?? '', explanation: requestWithdrawalExplanationById[text(item.id)] ?? '' })}>Withdraw request</button>
                  </div>
                </details>
              </div>
            </div> : null}
          </article>)}
        </div> : <p className="muted">No information has been requested.</p>}

      {isOpen && coordination?.canManageInformationRequests ? <div className="information-request-cta">
        <div><h3>Need more information?</h3><p>Ask the AI Use owner a question before completing your review.</p></div>
        <details className="information-request-create">
          <summary className="button secondary">Request information</summary>
          <InformationRequestComposer
            requirementId={requirementId}
            members={coordination.eligibleRecipients.map((member) => ({ membershipId: text(member.membershipId), displayName: text(member.displayName) }))}
            onChanged={refresh}
          />
        </details>
      </div> : null}
      </section>

      {isOpen ? <DecisionBriefSection
        icon="scale"
        eyebrow="Your decision"
        title="Your confirmation"
        description={confirmationType === 'security' ? 'Based on your security review, are the security arrangements adequate?' : 'Choose the determination that reflects your specialist review.'}
      >
        <section className="decision-summary-card" aria-labelledby="confirmation-summary-heading">
          <h3 id="confirmation-summary-heading">Before you respond</h3>
          <dl>
            <div><dt>Your role</dt><dd>{reviewerRole}</dd></div>
            <div><dt>You are confirming</dt><dd>{confirmationType === 'security' ? 'Whether the security arrangements are adequate' : taskTitle}</dd></div>
            <div><dt>You are not approving</dt><dd>The overall AI use</dd></div>
            <div><dt>Open information requests</dt><dd>{openInfo.length}</dd></div>
          </dl>
        </section>
        <form className="form-stack decision-brief-form" onSubmit={submitResponse}>
          <fieldset disabled={openInfo.length > 0 || respondMutation.isPending}>
            <legend>Your determination</legend>
            {decisions.map((option) => <label key={option.code}>
              <input type="radio" name="specialist-decision" value={option.code} checked={decision === option.code} onChange={() => setDecision(option.code)} />
              <span><strong>{option.label}</strong><small>{option.description}</small></span>
            </label>)}
          </fieldset>
          {decision === 'changed' ? <label>Corrected fact
            <textarea rows={3} value={changedValue} onChange={(event) => setChangedValue(event.target.value)} required />
          </label> : null}
          {needsCondition ? <label>{confirmationType === 'security' ? 'What needs to be done?' : 'Conditions'}
            <textarea rows={4} value={conditions} onChange={(event) => setConditions(event.target.value)} required />
            {confirmationType === 'security' ? <small>Describe the security conditions that must be completed.</small> : null}
          </label> : null}
          {needsReason ? <label>{confirmationType === 'security' ? 'What needs to be resolved?' : 'Reason'}
            <textarea rows={4} value={reason} onChange={(event) => setReason(event.target.value)} required />
            {confirmationType === 'security' ? <small>Explain the security issues that prevent these arrangements from being adequate.</small> : null}
          </label> : null}
          {confirmationType !== 'security' || decision === 'acceptable' ? <label>Comments <span className="muted">(optional)</span>
            <textarea rows={3} value={comments} onChange={(event) => setComments(event.target.value)} />
            {confirmationType === 'security' ? <small>Add any context that will help the people making the final approval decision.</small> : null}
          </label> : null}
          <label>Supporting evidence <span className="muted">(optional)</span>
            <input value={evidenceReference} onChange={(event) => setEvidenceReference(event.target.value)} placeholder="Supporting evidence (optional)" />
            <small>Add a document, ticket or evidence reference if it supports your response.</small>
          </label>
          <button className="button primary" type="submit" disabled={!decision || openInfo.length > 0 || respondMutation.isPending}>{respondMutation.isPending ? 'Recording…' : 'Complete confirmation'}</button>
        </form>
      </DecisionBriefSection> : <DecisionBriefSection icon="check" title="Confirmation complete" description="Your determination has been recorded in the Formal Assessment and cannot be overwritten." tone="positive">
        <p>Corrections use a new Assessment cycle so this determination remains part of the governed evidence.</p>
        {coordination?.canCoordinateConfirmation ? <details className="confirmation-reassessment">
          <summary>Start reassessment</summary>
          <form className="form-stack" onSubmit={(event) => { event.preventDefault(); reassessmentMutation.mutate() }}>
            <p>Describe what needs correction or which part of the decision basis has changed. Swell will create a new governed Assessment cycle and retain this completed determination unchanged.</p>
            <label>Reason for reassessment
              <textarea rows={4} required value={reassessmentReason} onChange={(event) => setReassessmentReason(event.target.value)} />
            </label>
            <button className="button secondary" type="submit" disabled={!reassessmentReason.trim() || reassessmentMutation.isPending}>{reassessmentMutation.isPending ? 'Starting…' : 'Create reassessment'}</button>
          </form>
        </details> : null}
      </DecisionBriefSection>}

      {coordination?.history.length ? <details className="panel confirmation-history"><summary>Assignment and request history</summary><ol>
        {coordination.history.map((event, index) => <li key={`${text(event.eventType)}-${text(event.occurredAt)}-${index}`}><strong>{customerHistoryLabel(event.eventType)}</strong><span>{new Date(text(event.occurredAt)).toLocaleString('en-AU')}</span></li>)}
      </ol></details> : null}
      </div>
    </div>
  )
}
